import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-landing',
  imports: [CommonModule, FormsModule],
  templateUrl: './landing.html',
  styleUrl: './landing.css'
})
export class Landing implements OnInit, OnDestroy {
  // This is a zoneless app, so async callbacks must notify Angular to refresh the view.
  private cdr = inject(ChangeDetectorRef);

  // External HubSpot consultation form
  consultationUrl = 'https://evsde.share-eu1.hsforms.com/29NSdoqGiTC2-JRtKM0Xx3A';

  // HubSpot embed script for the in-page contact form (.hs-form-frame in the template).
  // The script carries a MutationObserver, so once loaded it auto-renders the form
  // even when this component is created on SPA navigation.
  private readonly hubspotScriptSrc = 'https://js-eu1.hsforms.net/forms/embed/24997730.js';

  // Brochure download – OTP flow (OTP is generated, sent & verified server-side)
  private readonly apiBase = '/api';
  private readonly brochureUrl = 'assets-landing/landing-final.pdf';
  showBrochureModal = false;
  brochureStep: 'mobile' | 'otp' = 'mobile';
  brochureMobile = '';
  enteredOtp = '';
  otpError = '';
  isSendingOtp = false;
  isVerifyingOtp = false;
  resendCooldown = 0;
  private cooldownTimer: ReturnType<typeof setInterval> | null = null;

  services = [
    {
      title: 'Bulk SMS Transactional',
      subtitle: 'Informational SMS',
      description: 'Send important alerts, OTPs, order confirmations, appointment reminders, payment notifications, and service updates instantly.',
      icon: 'transactional'
    },
    {
      title: 'Bulk SMS Promotional',
      subtitle: 'Marketing SMS',
      description: 'Promote offers, discounts, new product launches, events, and campaigns to thousands of customers within seconds.',
      icon: 'promotional'
    },
    {
      title: 'WhatsApp Business Solutions',
      subtitle: 'API & Bulk Messaging',
      description: 'WhatsApp Bulk Messaging, API Integration, Automated Customer Communication, and Customer Support & Engagement.',
      icon: 'whatsapp'
    },
    {
      title: 'DLT Registration Support',
      subtitle: 'Compliance Made Easy',
      description: 'Get hassle-free DLT registration assistance and start sending compliant SMS campaigns quickly.',
      icon: 'dlt'
    },
    {
      title: 'Voice SMS Services',
      subtitle: 'Automated Voice Calls',
      description: 'Reach customers through automated voice calls and recorded promotional messages.',
      icon: 'voice'
    },
    {
      title: 'SMS API Integration',
      subtitle: 'For Apps & Platforms',
      description: 'Integrate SMS functionality directly into your website, CRM, ERP, mobile app, or software platform.',
      icon: 'api'
    },
    {
      title: 'SIM-Based Bulk SMS',
      subtitle: 'Personalized Marketing',
      description: 'Affordable SMS solutions for businesses looking for personalized SMS marketing.',
      icon: 'sim'
    },
    {
      title: 'School & College SMS',
      subtitle: 'Education Solutions',
      description: 'Send attendance updates, exam notifications, fee reminders, circulars, and emergency announcements to students and parents.',
      icon: 'school'
    }
  ];

  industries = [
    'Educational Institutions',
    'Hospitals & Clinics',
    'E-commerce Stores',
    'Retail Businesses',
    'Real Estate Companies',
    'Financial Services',
    'Travel & Tourism',
    'Government Organizations',
    'Startups & SMEs'
  ];

  smsReasons = [
    { stat: '98%', label: 'Open Rate' },
    { stat: 'Instant', label: 'Delivery' },
    { stat: 'Low Cost', label: 'Effective Marketing' },
    { stat: 'High', label: 'Customer Engagement' },
    { stat: 'Better', label: 'Conversion Rates' },
    { stat: 'Direct', label: 'Customer Reach' }
  ];

  ngOnInit() {
    this.loadHubspotForm();
  }

  // Inject the HubSpot embed script once. If it's already on the page (e.g. the
  // user navigated back to this route), HubSpot's own observer renders the new
  // .hs-form-frame, so we don't add it twice.
  private loadHubspotForm() {
    if (document.querySelector(`script[src="${this.hubspotScriptSrc}"]`)) return;
    const script = document.createElement('script');
    script.src = this.hubspotScriptSrc;
    script.defer = true;
    document.body.appendChild(script);
  }

  openBrochureModal() {
    this.showBrochureModal = true;
    this.brochureStep = 'mobile';
    this.brochureMobile = '';
    this.enteredOtp = '';
    this.otpError = '';
    this.clearCooldown();
  }

  closeBrochureModal() {
    this.showBrochureModal = false;
    this.clearCooldown();
  }

  // Keep only digits, capped at 10, so the field can never hold anything else
  onMobileInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 10);
    this.brochureMobile = digits;
    input.value = digits;
  }

  // Block non-numeric keystrokes before they reach the field
  blockNonNumeric(event: KeyboardEvent) {
    // Allow control/navigation keys; block anything that isn't a single digit
    if (event.key.length === 1 && !/\d/.test(event.key)) {
      event.preventDefault();
    }
  }

  // Sanitise pasted content down to digits
  onMobilePaste(event: ClipboardEvent) {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') ?? '';
    this.brochureMobile = pasted.replace(/\D/g, '').slice(0, 10);
  }

  sendOtp() {
    this.otpError = '';
    const mobile = this.brochureMobile.trim();
    // Valid Indian mobile: exactly 10 digits, starting 6-9
    if (!/^[6-9]\d{9}$/.test(mobile)) {
      this.otpError = 'Please enter a valid 10-digit mobile number.';
      return;
    }

    // Server generates the OTP and sends it via the SMS provider. The code
    // never reaches the browser — we only learn whether sending succeeded.
    this.isSendingOtp = true;
    fetch(`${this.apiBase}/otp/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mobile })
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({} as any));
        this.isSendingOtp = false;
        if (!res.ok || !data.success) {
          this.otpError = data.error || 'Could not send OTP. Please try again.';
          this.cdr.markForCheck();
          return;
        }
        this.enteredOtp = '';
        this.brochureStep = 'otp';
        this.startResendCooldown(data.cooldown || 30);
        this.cdr.markForCheck();
      })
      .catch(() => {
        this.isSendingOtp = false;
        this.otpError = 'Could not send OTP. Please try again.';
        this.cdr.markForCheck();
      });
  }

  verifyOtp() {
    this.otpError = '';
    const otp = this.enteredOtp.trim();
    if (!/^\d{6}$/.test(otp)) {
      this.otpError = 'Please enter the 6-digit OTP.';
      return;
    }

    // Verification happens server-side against the stored OTP.
    this.isVerifyingOtp = true;
    fetch(`${this.apiBase}/otp/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mobile: this.brochureMobile.trim(), otp })
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({} as any));
        this.isVerifyingOtp = false;
        if (!res.ok || !data.verified) {
          this.otpError = data.error || 'Invalid OTP. Please try again.';
          this.cdr.markForCheck();
          return;
        }
        this.downloadBrochure();
        this.closeBrochureModal();
        this.cdr.markForCheck();
      })
      .catch(() => {
        this.isVerifyingOtp = false;
        this.otpError = 'Could not verify OTP. Please try again.';
        this.cdr.markForCheck();
      });
  }

  private startResendCooldown(seconds: number) {
    this.clearCooldown();
    this.resendCooldown = seconds;
    this.cooldownTimer = setInterval(() => {
      this.resendCooldown -= 1;
      if (this.resendCooldown <= 0) {
        this.clearCooldown();
      }
      this.cdr.markForCheck();
    }, 1000);
  }

  private clearCooldown() {
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    }
    this.resendCooldown = 0;
  }

  ngOnDestroy() {
    this.clearCooldown();
  }

  private downloadBrochure() {
    const link = document.createElement('a');
    link.href = this.brochureUrl;
    link.download = 'XpressSMS-Brochure.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
